"use client";

import { useEffect, useRef } from "react";
import { brand, contact, contactNavItem, mainNav } from "@/content/site";
import { whatsappUrl } from "@/content/whatsapp";
import { TransitionLink } from "./TransitionLink";
import styles from "./MobileMenu.module.css";

type Props = {
  open: boolean;
  onClose: (refocus: boolean) => void;
  isActive: (href: string) => boolean;
};

// Menú de celular a pantalla completa (guía 5): el mundo queda detrás, desenfocado, y
// los enlaces aparecen grandes, de a uno. Es un diálogo modal: el foco queda adentro
// mientras está abierto y Escape lo cierra.
export function MobileMenu({ open, onClose, isActive }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const root = ref.current;
    const first = root?.querySelector<HTMLElement>("a, button");
    first?.focus();
    const prevOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose(true);
        return;
      }
      if (e.key !== "Tab" || !root) return;
      const items = Array.from(root.querySelectorAll<HTMLElement>("a, button"));
      if (!items.length) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.documentElement.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  const items = [...mainNav, contactNavItem];

  return (
    <div
      ref={ref}
      id="menu-movil"
      className={styles.menu}
      role="dialog"
      aria-modal="true"
      aria-label="Menú"
      data-open={open ? "true" : undefined}
      hidden={!open}
    >
      <nav aria-label="Principal (celular)">
        <ul className={styles.list}>
          {items.map((item, i) => (
            <li key={item.href} style={{ "--i": i } as React.CSSProperties}>
              <TransitionLink
                href={item.href}
                className={styles.item}
                aria-current={isActive(item.href) ? "page" : undefined}
                onClick={() => onClose(false)}
              >
                {item.label}
              </TransitionLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className={styles.foot}>
        {/* El buscador lo abre EasterRoot (delegación por data-egg-open); el menú se cierra. */}
        <button type="button" className={styles.search} data-egg-open="search" onClick={() => onClose(false)}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="10.5" cy="10.5" r="6" />
            <path d="m15 15 4.5 4.5" />
          </svg>
          Buscar en el sitio
        </button>
        <a className={styles.whatsapp} href={whatsappUrl("general")} rel="noopener" target="_blank">
          Escribinos por WhatsApp
        </a>
        <a className={styles.mail} href={`mailto:${contact.email}`}>
          {contact.email}
        </a>
        <p className={styles.sign}>{brand.signature}</p>
      </div>
    </div>
  );
}
